import { useState } from "react";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../../i18n/I18nProvider";

const SERVICE_SLUGS = [
  "modellazione-3d",
  "prototipazione-stampa-3d",
  "ambienti-digitali",
  "supporto-digitale",
  "altro",
];

const TOTAL = 5;

const Chip = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-[11px] md:text-[12px] font-mono tracking-[0.14em] md:tracking-[0.16em] uppercase px-3 md:px-4 py-2.5 rounded-full border transition-all ${
      selected
        ? "bg-ink-900 text-cream border-ink-900"
        : "bg-transparent text-ink-700 border-ink-200 hover:border-ink-900"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div>
    <label className="overline block mb-2 md:mb-3">{label}</label>
    {children}
  </div>
);

export const QuoteWizard = ({ form, busy, update, setField, submit, services }) => {
  const t = useT();
  const w = t("contact.wizard");
  const [step, setStep] = useState(1);

  const canProceed = () => {
    if (step === 2) return !!form.budget;
    if (step === 3) return !!form.timeline;
    if (step === 4) return !!form.message.trim();
    return true;
  };

  const next = () => {
    if (!canProceed()) {
      toast.error(step === 4 ? t("contact.form.validationError") : w.selectHint);
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL));
  };

  const titles = [w.step1Title, w.step2Title, w.step3Title, w.step4Title, w.step5Title];

  return (
    <div data-testid={`quote-step-${step}`}>
      {/* Progress */}
      <div className="flex items-center justify-between mb-2">
        <span className="overline" data-testid="quote-progress">
          {w.stepWord} {step} {w.ofWord} {TOTAL}
        </span>
      </div>
      <div className="h-px bg-ink-200 mb-6 md:mb-8">
        <div
          className="h-px bg-ink-900 transition-all duration-500"
          style={{ width: `${(step / TOTAL) * 100}%` }}
        />
      </div>

      <h2 className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tightest mb-6 md:mb-8">
        {titles[step - 1]}
      </h2>

      {step === 1 && (
        <div className="flex flex-wrap gap-2">
          {services.map((s, i) => (
            <Chip
              key={SERVICE_SLUGS[i]}
              selected={form.subject === s}
              onClick={() => setField("subject", s)}
              testId={`contact-service-${SERVICE_SLUGS[i]}`}
            >
              {s}
            </Chip>
          ))}
        </div>
      )}

      {step === 2 && (
        <>
          <div className="flex flex-wrap gap-2">
            {w.budgetOptions.map((b, i) => (
              <Chip
                key={b}
                selected={form.budget === b}
                onClick={() => setField("budget", b)}
                testId={`quote-budget-${i}`}
              >
                {b}
              </Chip>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-ink-500">{w.step2Hint}</p>
        </>
      )}

      {step === 3 && (
        <div className="flex flex-wrap gap-2">
          {w.timelineOptions.map((tl, i) => (
            <Chip
              key={tl}
              selected={form.timeline === tl}
              onClick={() => setField("timeline", tl)}
              testId={`quote-timeline-${i}`}
            >
              {tl}
            </Chip>
          ))}
        </div>
      )}

      {step === 4 && (
        <Field label={t("contact.form.messageLabel")}>
          <textarea
            rows={6}
            value={form.message}
            onChange={update("message")}
            data-testid="contact-message"
            className="w-full bg-transparent border-0 border-b border-ink-200 focus:border-ink-900 outline-none py-3 text-[15px] md:text-[16px] leading-relaxed transition-colors resize-none"
            placeholder={t("contact.form.messagePlaceholder")}
          />
        </Field>
      )}

      {step === 5 && (
        <div className="space-y-6">
          <div className="card-flat p-4 md:p-5 bg-paper" data-testid="quote-summary">
            <div className="overline mb-3">{w.summaryTitle}</div>
            <div className="flex flex-wrap gap-2">
              {[form.subject, form.budget, form.timeline].map((v) => (
                <span
                  key={v}
                  className="text-[10px] font-mono tracking-[0.16em] uppercase px-3 py-1.5 border border-ink-200 rounded-full text-ink-700 bg-white"
                >
                  {v}
                </span>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <Field label={t("contact.form.nameLabel")}>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                data-testid="contact-name"
                className="w-full bg-transparent border-0 border-b border-ink-200 focus:border-ink-900 outline-none py-3 text-base md:text-lg font-display transition-colors"
                placeholder={t("contact.form.namePlaceholder")}
              />
            </Field>
            <Field label={t("contact.form.emailLabel")}>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                data-testid="contact-email"
                className="w-full bg-transparent border-0 border-b border-ink-200 focus:border-ink-900 outline-none py-3 text-base md:text-lg font-display transition-colors"
                placeholder={t("contact.form.emailPlaceholder")}
              />
            </Field>
          </div>
          <p className="text-[12px] text-ink-500 max-w-md">{t("contact.form.hint")}</p>
        </div>
      )}

      {/* Nav */}
      <div className="mt-8 md:mt-10 flex items-center justify-between gap-4">
        {step > 1 ? (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            data-testid="quote-back-btn"
            className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.18em] uppercase text-ink-500 hover:text-ink-900 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6} /> {w.back}
          </button>
        ) : (
          <span />
        )}
        {step < TOTAL ? (
          <button
            type="button"
            onClick={next}
            data-testid="quote-next-btn"
            className="btn-pill"
          >
            {w.next}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="contact-submit"
            className="btn-pill disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? t("contact.form.submitting") : t("contact.form.submit")}
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
          </button>
        )}
      </div>
    </div>
  );
};
